<template>
  <div class="chat-message-row">
    <!-- 이미지 모달 -->
    <div v-if="showImageModal" class="image-modal" @click="closeImageModal">
      <div class="image-modal-content">
        <img :src="modalImage" alt="확대된 이미지" />
        <button class="close-modal-btn" @click.stop="closeImageModal">
          <i class="ri-close-line"></i>
        </button>
      </div>
    </div>

    <!-- AI 어시스턴트 메시지 (왼쪽에 표시) -->
    <div v-if="message.type === 'bot'" class="d-flex align-items-start mb-3">
      <div class="message-avatar me-2">
        <div class="bot-avatar">
          <i class="ri-robot-line"></i>
        </div>
      </div>
      <div class="message-bubble bot-bubble">
        <div class="message-header small mb-1">
          <span class="fw-bold">AI 어시스턴트</span>
          <span class="message-time ms-2 text-muted">{{ formatTime(message.timestamp) }}</span>
        </div>
        <!-- 이미지가 있는 경우 -->
        <div v-if="message.image" class="message-image">
          <img :src="message.image" alt="첨부 이미지" @click="openImageModal(message.image)" />
        </div>
        <!-- 여러 이미지가 있는 경우 -->
        <div v-if="message.images && message.images.length > 0" class="message-images">
          <div v-for="(image, index) in message.images" :key="index" class="message-image">
            <img :src="image" alt="첨부 이미지" @click="openImageModal(image)" />
          </div>
        </div>
        <div class="message-text" :class="{'markdown-content': message.isMarkdown}" v-html="message.isMarkdown ? message.content : formatMessage(message.content)"></div>
      </div>
    </div>

    <!-- 사용자 메시지 (오른쪽에 표시) -->
    <div v-else class="d-flex align-items-start justify-content-end mb-3">
      <div class="message-bubble user-bubble">
        <div class="message-header small mb-1 text-end">
          <span class="message-time me-2 text-muted">{{ formatTime(message.timestamp) }}</span>
          <span class="fw-bold">사용자</span>
        </div>
        <!-- 이미지가 있는 경우 -->
        <div v-if="message.image" class="message-image">
          <img :src="message.image" alt="첨부 이미지" @click="openImageModal(message.image)" />
        </div>
        <!-- 여러 이미지가 있는 경우 -->
        <div v-if="message.images && message.images.length > 0" class="message-images">
          <div v-for="(image, index) in message.images" :key="index" class="message-image">
            <img :src="image" alt="첨부 이미지" @click="openImageModal(image)" />
          </div>
        </div>
        <div v-if="message.content" class="message-text" v-html="message.isMarkdown ? message.content : formatMessage(message.content)"></div>
      </div>
      <div class="message-avatar ms-2">
        <div class="user-avatar">
          <i class="ri-user-line"></i>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ChatMessage',
  props: {
    message: {
      type: Object,
      required: true
    },
    isLast: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showImageModal: false,
      modalImage: null
    };
  },
  methods: {
    formatTime(timestamp) {
      const date = new Date(timestamp);
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    },
    formatMessage(text) {
      // 줄바꿈을 <br>로 변환
      return text.replace(/\n/g, '<br>');
    },
    // 이미지 클릭 시 모달 열기
    openImageModal(imageSrc) {
      this.modalImage = imageSrc;
      this.showImageModal = true;
      document.body.style.overflow = 'hidden'; // 모달 열리면 스크롤 방지
    },
    // 모달 닫기
    closeImageModal() {
      this.showImageModal = false;
      document.body.style.overflow = ''; // 스크롤 다시 활성화
    }
  }
};
</script>

<style scoped>
.chat-message-row {
  margin-bottom: 8px;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message-avatar {
  flex-shrink: 0;
}

.bot-avatar, .user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.bot-avatar {
  background-color: #6c757d; /* 중립적인 회색 */
  color: white;
}

.user-avatar {
  background-color: #6c757d; /* 중립적인 회색 */
  color: white;
}

.message-bubble {
  max-width: 70%;
  border-radius: 16px;
  padding: 2px;
}

.message-header {
  padding: 4px 12px 0 12px;
}

.message-text {
  padding: 4px 12px 6px;
  font-size: 15px;
  line-height: 1.3;
  white-space: pre-wrap;
  word-break: break-word;
}

.message-text a {
  color: #007bff;
  text-decoration: none;
  font-weight: 500;
}

.message-text a:hover {
  text-decoration: underline;
}

.bot-bubble {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-top-left-radius: 4px;
}

.bot-bubble .message-text {
  color: #212529;
}

.user-bubble {
  background-color: #e9ecef;
  border: 1px solid #dee2e6;
  border-top-right-radius: 4px;
}

.user-bubble .message-text {
  color: #212529;
  text-align: right;
}

/* 코드 블록 스타일링 */
.message-text pre {
  background-color: #343a40;
  color: #f8f9fa;
  padding: 12px;
  border-radius: 6px;
  overflow-x: auto;
  margin: 8px 0;
}

.message-text code {
  font-family: 'Courier New', monospace;
  font-size: 14px;
}

/* 이미지 스타일 */
.message-image {
  margin-bottom: 8px;
}

.message-image img {
  max-width: 200px;
  max-height: 200px;
  border-radius: 8px;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.message-image img:hover {
  opacity: 0.9;
}

/* 여러 이미지 스타일 */
.message-images {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.message-images .message-image {
  margin-bottom: 0;
}

.message-images .message-image img {
  width: 120px;
  height: 120px;
  object-fit: cover;
}

/* 이미지 모달 */
.image-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.image-modal-content {
  position: relative;
  max-width: 90%;
  max-height: 90%;
}

.image-modal-content img {
  max-width: 100%;
  max-height: 90vh;
  border-radius: 4px;
}

.close-modal-btn {
  position: absolute;
  top: -40px;
  right: 0;
  background-color: transparent;
  color: white;
  border: none;
  font-size: 24px;
  cursor: pointer;
}


/* 마크다운 스타일 */
:deep(.markdown-content) {
  line-height: 1.6;
}

:deep(.markdown-content h1) {
  font-size: 1.8em;
  margin-top: 16px;
  margin-bottom: 12px;
  font-weight: 600;
}

:deep(.markdown-content h2) {
  font-size: 1.5em;
  margin-top: 14px;
  margin-bottom: 10px;
  font-weight: 600;
}

:deep(.markdown-content h3) {
  font-size: 1.3em;
  margin-top: 12px;
  margin-bottom: 8px;
  font-weight: 600;
}

:deep(.markdown-content h4) {
  font-size: 1.2em;
  margin-top: 10px;
  margin-bottom: 6px;
  font-weight: 600;
}

:deep(.markdown-content p) {
  margin-bottom: 10px;
}

:deep(.markdown-content ul), :deep(.markdown-content ol) {
  padding-left: 20px;
  margin-bottom: 10px;
}

:deep(.markdown-content li) {
  margin-bottom: 4px;
}

:deep(.markdown-content blockquote) {
  border-left: 4px solid #e9ecef;
  padding-left: 16px;
  margin-left: 0;
  margin-right: 0;
  font-style: italic;
  color: #6c757d;
}

:deep(.markdown-content code) {
  background-color: #f8f9fa;
  padding: 2px 4px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 0.9em;
}

:deep(.markdown-content pre) {
  background-color: #343a40;
  color: #f8f9fa;
  padding: 12px;
  border-radius: 6px;
  overflow-x: auto;
  margin: 8px 0;
}

:deep(.markdown-content pre code) {
  background-color: transparent;
  padding: 0;
  color: inherit;
}

:deep(.markdown-content a) {
  color: #007bff;
  text-decoration: none;
}

:deep(.markdown-content a:hover) {
  text-decoration: underline;
}

:deep(.markdown-content table) {
  border-collapse: collapse;
  width: 100%;
  margin-bottom: 16px;
}

:deep(.markdown-content th), :deep(.markdown-content td) {
  border: 1px solid #dee2e6;
  padding: 8px 12px;
  text-align: left;
}

:deep(.markdown-content th) {
  background-color: #f8f9fa;
  font-weight: 600;
}

:deep(.markdown-content tr:nth-child(even)) {
  background-color: #f8f9fa;
}

:deep(.markdown-content img) {
  max-width: 100%;
  border-radius: 4px;
  margin: 8px 0;
}

:deep(.markdown-content hr) {
  border: 0;
  border-top: 1px solid #dee2e6;
  margin: 16px 0;
}
/* 마크다운 스타일 */
:deep(.markdown-content) {
  line-height: 1.3;
}

:deep(.markdown-content h1),
:deep(.markdown-content h2),
:deep(.markdown-content h3),
:deep(.markdown-content h4) {
  margin-top: 4px;
  margin-bottom: 2px;
  font-weight: 600;
}

:deep(.markdown-content h1) {
  font-size: 1.5em;
}

:deep(.markdown-content h2) {
  font-size: 1.3em;
}

:deep(.markdown-content h3) {
  font-size: 1.2em;
}

:deep(.markdown-content h4) {
  font-size: 1.1em;
}

:deep(.markdown-content p) {
  margin-bottom: 0;
  margin-top: 0;
}

:deep(.markdown-content ul),
:deep(.markdown-content ol) {
  padding-left: 20px;
  margin-top: 0;
  margin-bottom: 0;
}

:deep(.markdown-content li) {
  margin-bottom: 0;
  line-height: 1.3;
}

:deep(.markdown-content blockquote) {
  border-left: 4px solid #e9ecef;
  padding-left: 16px;
  margin-left: 0;
  margin-right: 0;
  font-style: italic;
  color: #6c757d;
}

:deep(.markdown-content code) {
  background-color: #f8f9fa;
  padding: 2px 4px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 0.9em;
}

:deep(.markdown-content pre) {
  background-color: #343a40;
  color: #f8f9fa;
  padding: 12px;
  border-radius: 6px;
  overflow-x: auto;
  margin: 8px 0;
}

:deep(.markdown-content pre code) {
  background-color: transparent;
  padding: 0;
  color: inherit;
}

:deep(.markdown-content table) {
  border-collapse: collapse;
  width: 100%;
  margin-bottom: 16px;
}

:deep(.markdown-content th),
:deep(.markdown-content td) {
  border: 1px solid #dee2e6;
  padding: 8px 12px;
  text-align: left;
}

:deep(.markdown-content th) {
  background-color: #f8f9fa;
  font-weight: 600;
}

:deep(.markdown-content tr:nth-child(even)) {
  background-color: #f8f9fa;
}

:deep(.markdown-content img) {
  max-width: 100%;
  border-radius: 4px;
  margin: 8px 0;
}

:deep(.markdown-content hr) {
  border: 0;
  border-top: 1px solid #dee2e6;
  margin: 16px 0;
}
</style>
